import {useMemo, useRef, useState} from "react";
import {fileApi} from "../../../services/fileService";
import {buildImageUrl} from "../../../utils/imageUrlBuilder";
import {documentType, fileTypeRelation} from "../../../utils/constants";
import UpImage from "../../../images/image-upload.svg";
import UploadPreviewState from "../shared/UploadPreviewState";
import {showToastModal} from "../shared/ToastModalSwal";

const MAX_FILES = 5;
const MAX_SIZE = 10 * 1024 * 1024;

const RealizationGallery = ({
  files = [],
  technicianId,
  editable = false,
  onChanged,
}) => {
  const inputRef = useRef(null);
  const [uploads, setUploads] = useState([]);
  const [selected, setSelected] = useState(null);
  const realizations = useMemo(
    () => files.filter((file) => file.type === documentType.REALIZATION),
    [files],
  );

  const updateUpload = (key, values) =>
    setUploads((current) =>
      current.map((upload) => upload.key === key ? {...upload, ...values} : upload),
    );

  const uploadOne = async (file, key) => {
    const formData = new FormData();
    formData.append("fileUpload", file);
    formData.append("type", documentType.REALIZATION);
    formData.append("fileUploadId", technicianId);
    formData.append("fileUploadType", fileTypeRelation.technician);
    try {
      await fileApi.add(formData, ({loaded, total}) => {
        updateUpload(key, {progress: total ? Math.round((loaded / total) * 100) : 0});
      });
      updateUpload(key, {progress: 100, status: "done"});
    } catch (error) {
      updateUpload(key, {
        status: "error",
        error: error?.response?.data?.error || error?.message || "Échec de l’envoi",
      });
    }
  };

  const selectFiles = async (event) => {
    const selectedFiles = [...event.target.files];
    event.target.value = "";
    if (selectedFiles.length > MAX_FILES) {
      showToastModal({
        success: false,
        title: "Maximum 5 images à la fois.",
        message: "Sélectionnez au maximum 5 images, puis recommencez.",
      });
      return;
    }
    const entries = selectedFiles.map((file, index) => ({
      key: `${Date.now()}-${index}`,
      file,
      progress: 0,
      previewUrl: URL.createObjectURL(file),
      status: file.type.startsWith("image/") && file.size <= MAX_SIZE
        ? "uploading" : "error",
      error: !file.type.startsWith("image/")
        ? "Ce fichier n’est pas une image."
        : file.size > MAX_SIZE ? "La taille maximale est de 10 Mo par image." : "",
    }));
    setUploads(entries);
    await Promise.all(entries
      .filter(({status}) => status === "uploading")
      .map(({file, key}) => uploadOne(file, key)));
    await onChanged?.();
    window.setTimeout(() => {
      setUploads((current) => {
        current
          .filter(({status}) => status === "done")
          .forEach(({previewUrl}) => previewUrl && URL.revokeObjectURL(previewUrl));
        return current.filter(({status}) => status === "error");
      });
    }, 1000);
  };

  const remove = async (id) => {
    await fileApi.delete({id});
    await onChanged?.();
  };

  const currentIndex = selected == null
    ? -1 : realizations.findIndex(({id}) => id === selected);
  return (
    <div className="w-full">
      {editable && (
        <div className="mb-5">
          <h2 className="text-xl font-semibold text-[#19295C]">Mes réalisations</h2>
          <p className="mt-1 mb-4 text-sm text-[#667085]">
            Ajoutez jusqu’à 5 images à la fois, 10 Mo maximum par image.
            Le nombre total de réalisations n’est pas limité. Elles seront
            publiées après validation du backoffice.
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={selectFiles}
          />
          <button
            type="button"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              selectFiles({target: {files: event.dataTransfer.files, value: ""}});
            }}
            className="flex min-h-[180px] w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#1A64B9] bg-white px-4"
            disabled={uploads.some(({status}) => status === "uploading")}
            onClick={() => inputRef.current?.click()}
          >
            <img src={UpImage} alt="" className="h-14 w-14" />
            <span className="mt-2 font-semibold text-[#19295C]">
              Glissez vos images ici ou cliquez pour parcourir
            </span>
            <span className="mt-1 text-xs text-[#667085]">
              JPG, PNG ou WEBP · 5 fichiers à la fois · 10 Mo max par image
            </span>
          </button>
          {!!uploads.length && (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {uploads.map(({key, file, previewUrl, progress, status, error}) => (
                <div key={key}>
                  <UploadPreviewState
                    uploading={status === "uploading"}
                    progress={progress}
                    error={error}
                    className="aspect-square border border-[#D6E2EF] bg-white"
                  >
                    <img src={previewUrl} alt={file.name} className="h-full w-full object-contain" />
                  </UploadPreviewState>
                  <div className="mt-1 truncate text-xs text-[#667085]">{file.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className={`grid gap-2 ${editable ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" : "grid-cols-3"}`}>
        {realizations.map((file) => (
          <div key={file.id} className="relative aspect-square rounded-lg border overflow-hidden bg-white">
            <button type="button" aria-label="Visualiser la réalisation" className="h-full w-full" onClick={() => setSelected(file.id)}>
              <img
                src={buildImageUrl(file.path)}
                alt={file.name || "Réalisation"}
                className="h-full w-full object-cover bg-gray-50"
              />
            </button>
            {editable && (
              <>
                <span className="absolute left-1 bottom-1 rounded bg-white/90 px-1 text-[10px]">
                  {file.validationStatus === "APPROVED"
                    ? "Validée"
                    : file.validationStatus === "REJECTED" ? "Refusée" : "En attente"}
                </span>
                <button
                  type="button"
                  aria-label="Supprimer"
                  className="absolute right-1 top-1 rounded-full bg-red-500 text-white w-6 h-6"
                  onClick={() => remove(file.id)}
                >×</button>
              </>
            )}
          </div>
        ))}
      </div>
      {!realizations.length && editable && (
        <p className="text-[#7e7e7e] mt-4">Aucune réalisation ajoutée.</p>
      )}
      {currentIndex >= 0 && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-5"
          onClick={() => setSelected(null)}
        >
          <button type="button" aria-label="Fermer la galerie" className="absolute top-5 right-6 text-white text-4xl">×</button>
          <button
            type="button"
            aria-label="Réalisation précédente"
            className="absolute left-5 text-white text-4xl"
            onClick={(event) => {
              event.stopPropagation();
              setSelected(realizations[(currentIndex - 1 + realizations.length) % realizations.length].id);
            }}
          >‹</button>
          <img
            src={buildImageUrl(realizations[currentIndex].path)}
            alt={realizations[currentIndex].name || "Réalisation"}
            className="max-w-[85vw] max-h-[85vh] object-contain"
            onClick={(event) => event.stopPropagation()}
          />
          <button
            type="button"
            aria-label="Réalisation suivante"
            className="absolute right-5 text-white text-4xl"
            onClick={(event) => {
              event.stopPropagation();
              setSelected(realizations[(currentIndex + 1) % realizations.length].id);
            }}
          >›</button>
        </div>
      )}
    </div>
  );
};

export default RealizationGallery;
